import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import MaterialService from "../../api/MaterialService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { MaterialsList } from "../../components/MaterialsList/MaterialsList";
import { SubMenu } from "../../components/SubMenu/SubMenu";
import { WrenchIcon } from "../../icons/WrenchIcon/WrenchIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import { fetchProducts, productSliceActions } from "../../store/products-slice";
import { searchActions } from "../../store/search-slice";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/choose-products.module.css";
import { MinimalProduct } from "../../types/product";

export const ChooseProducts = () => {
  const dispatch: ThunkDispatch<any, unknown, AnyAction> = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDeficiency = false, deficiencyDate } = location.state || {};
  const { editMode } = location.state || {};

  const [searchedProducts, setSearchedProducts] = useState<MinimalProduct[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  const monitor = useSelector((state: RootState) => state.monitor);
  const products = useSelector((state: RootState) => state.products);
  const appointment = useSelector((state: RootState) => state.appointment);
  const searchValue = useSelector((state: RootState) => state.search.value);
  const alreadySelectedProducts = useSelector(
    (state: RootState) => state.documentation.products
  );

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      await dispatch(fetchProducts());
      setLoading(false);
    };

    dispatch(activeTabsActions.setDashbaordUrl(location.pathname));

    if (products.length === 0) {
      loadProducts();
    }
  }, [
    appointment,
    dispatch,
    location.pathname,
    monitor.inProgress,
    products.length,
  ]);

  useEffect(() => {
    if (products.length === 0 || alreadySelectedProducts.length === 0) return;

    dispatch(
      productSliceActions.initializeQuantities(
        alreadySelectedProducts.map((p: any) => ({
          id: p.id,
          quantity: p.quantity,
          payExtraQuantity: p.payExtraQuantity ?? 0,
        }))
      )
    );
  }, [dispatch, products.length, alreadySelectedProducts]);

  const handleProductsChosen = async () => {
    if (editMode) {
      setSaving(true);
      try {
        // The summary editor owns the per-product job totals. Send the full desired list
        // in one go; the backend reconciles it idempotently (unchanged products are left
        // untouched, changed/removed products collapse to a single after-service figure),
        // so re-saving the same numbers can never inflate the totals.
        const materials = products
          .filter((p) => p.quantity > 0)
          .map((product) => ({
            source: product.isSevDeskProduct ? "sevdesk" : "web",
            ...(product.isSevDeskProduct
              ? {
                  sevdeskProductId: product.id,
                  name: product.name,
                  description: product.description,
                }
              : {
                  productId: product.id,
                  description: product.description,
                }),
            quantity: product.quantity,
            // invariant: you can never bill more than the quantity present
            payExtraQuantity: Math.min(
              product.payExtraQuantity ?? 0,
              product.quantity
            ),
          }));

        await MaterialService.SyncMaterials(appointment.jobs[0].id, {
          materials,
        });
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(searchActions.setIsExpanded(false));
        dispatch(searchActions.setSearchValue(""));
        dispatch(productSliceActions.resetState());
        setSaving(false);

        goOneStepBack(navigate);
      }
    } else {
      dispatch(searchActions.setIsExpanded(false));
      dispatch(searchActions.setSearchValue(""));

      goOneStepBack(navigate);
    }
  };

  useEffect(() => {
    if (searchValue.length >= 3) {
      const lowerCaseSearchValue = searchValue.toLowerCase();

      setSearchedProducts(
        products.filter((product: MinimalProduct) =>
          product.name?.toLowerCase().includes(lowerCaseSearchValue)
        )
      );
    } else if (searchValue.length === 0) {
      setSearchedProducts(products);
    }
  }, [searchValue, products]);

  return (
    <AppLayout hideHeader={true}>
      <div>
        <BackButtonHeader
          handleBackButtonClicked={() => goOneStepBack(navigate)}
          showDocumentation={false}
        />
        <div className={styles["content-wrapper"]}>
          <AppointmentDetails
            showMangelTag={isDeficiency}
            deficiencyDate={deficiencyDate ?? undefined}
          />

          {loading ? (
            <LoadingSpinner loading={loading}></LoadingSpinner>
          ) : (
            <div className={styles["content"]}>
              <SubMenu showSearchButton={true}>
                <span>Einsatzmaterial eintragen</span>
                <WrenchIcon color="dark" />
              </SubMenu>

              <MaterialsList title="Materialeinsatz" data={searchedProducts} />

              <FloatingButtons>
                <CustomButton
                  color="green"
                  loading={saving}
                  onClick={handleProductsChosen}
                >
                  Erledigt
                </CustomButton>
              </FloatingButtons>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};
